/**
 * Unified Analytics Tracking Utility
 * Green Filter USA - Conversion Tracking Implementation
 *
 * Sends events to:
 * - Google Tag Manager (GTM) → Google Analytics 4 (GA4)
 * - Facebook Pixel
 * - MarketingCloudFX
 */

import {
  trackFacebookViewContent,
  trackFacebookAddToCart,
  trackFacebookInitiateCheckout,
  trackFacebookSearch,
  trackFacebookLead,
  trackFacebookContact,
} from './facebookPixel';

// Extend Window interface to include dataLayer and tracking properties
declare global {
  interface Window {
    dataLayer: any[];
    pageStartTime: number;
    RCFX_SERIALIZED_URL_PARAMS: string;
    mcfx: any;
  }
}

// Initialize dataLayer if it doesn't exist (only in browser)
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

/**
 * Send a custom event to Google Tag Manager
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 */
export const trackEvent = (eventName: string, eventParams: Record<string, any> = {}) => {
  // Only track in browser environment
  if (typeof window === 'undefined' || !window.dataLayer) {
    return;
  }

  try {
    window.dataLayer.push({
      event: eventName,
      ...eventParams,
    });

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('GTM Event:', eventName, eventParams);
    }
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
};

/**
 * Track page view
 * @param {string} pagePath - Page path
 * @param {string} pageTitle - Page title
 */
export const trackPageView = (pagePath: string, pageTitle: string) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: typeof window !== 'undefined' ? window.location.href : '',
  });
};

interface Product {
  id?: string;
  gfu_part_num?: string;
  title?: string;
  price?: number | string;
  category?: string;
}

/**
 * Track product view (GA4 eCommerce + Facebook Pixel)
 * @param {object} product - Product details
 */
export const trackProductView = (product: Product) => {
  // Google Analytics 4
  trackEvent('view_item', {
    currency: 'USD',
    value: parseFloat(String(product.price)) || 0,
    items: [
      {
        item_id: product.gfu_part_num || product.id,
        item_name: product.title || 'Unknown Product',
        item_brand: 'Green Filter',
        item_category: product.category || 'Air Filters',
        price: parseFloat(String(product.price)) || 0,
        quantity: 1,
      },
    ],
  });

  // Facebook Pixel
  trackFacebookViewContent({
    id: product.gfu_part_num || product.id,
    title: product.title,
    price: product.price,
    category: product.category,
  });
};

/**
 * Track add to cart (GA4 eCommerce + Facebook Pixel)
 * @param {object} product - Product details
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (product: Product, quantity: number = 1) => {
  // Google Analytics 4
  trackEvent('add_to_cart', {
    currency: 'USD',
    value: parseFloat(String(product.price)) * quantity || 0,
    items: [
      {
        item_id: product.gfu_part_num || product.id,
        item_name: product.title || 'Unknown Product',
        item_brand: 'Green Filter',
        item_category: product.category || 'Air Filters',
        price: parseFloat(String(product.price)) || 0,
        quantity: quantity,
      },
    ],
  });

  // Facebook Pixel
  trackFacebookAddToCart(
    {
      id: product.gfu_part_num || product.id,
      title: product.title,
      price: product.price,
      category: product.category,
    },
    quantity
  );
};

interface CartData {
  total?: number | string;
  items?: any[];
  itemCount?: number;
}

/**
 * Track begin checkout (GA4 + Facebook Pixel)
 * @param {object} cartData - Cart details
 */
export const trackBeginCheckout = (cartData: CartData) => {
  // Google Analytics 4
  trackEvent('begin_checkout', {
    currency: 'USD',
    value: parseFloat(String(cartData.total)) || 0,
    items: cartData.items || [],
  });

  // Facebook Pixel
  trackFacebookInitiateCheckout({
    total: cartData.total,
    items: cartData.items,
    itemCount: cartData.itemCount,
  });
};

/**
 * Track form submission
 * @param {string} formName - Name of the form (e.g., 'contact', 'newsletter')
 * @param {object} formData - Form data (optional, don't send PII)
 */
export const trackFormSubmission = (formName: string, formData: Record<string, any> = {}) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_location: typeof window !== 'undefined' ? window.location.pathname : '',
    ...formData,
  });
};

/**
 * Track contact form submission (lead generation + Facebook Pixel)
 */
export const trackContactFormSubmit = () => {
  // Google Analytics 4
  trackEvent('generate_lead', {
    form_type: 'contact',
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });

  // Facebook Pixel
  trackFacebookLead('contact');
};

/**
 * Track search (GA4 + Facebook Pixel)
 * @param {string} searchTerm - Search query
 */
export const trackSearch = (searchTerm: string) => {
  // Google Analytics 4
  trackEvent('search', {
    search_term: searchTerm,
  });

  // Facebook Pixel
  trackFacebookSearch(searchTerm);
};

/**
 * Track click on phone number or email (potential leads + Facebook Pixel)
 * @param {string} contactType - 'phone' or 'email'
 */
export const trackContactClick = (contactType: 'phone' | 'email') => {
  // Google Analytics 4
  trackEvent('contact_click', {
    contact_type: contactType,
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });

  // Facebook Pixel
  trackFacebookContact(contactType);
};

/**
 * Track filter measurement tool usage
 */
export const trackMeasureToolUse = () => {
  trackEvent('measure_tool_use', {
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });
};

interface VehicleData {
  make?: string;
  model?: string;
  year?: string | number;
}

/**
 * Track vehicle selector usage
 */
export const trackVehicleSearch = (vehicleData: VehicleData) => {
  trackEvent('vehicle_search', {
    make: vehicleData.make || '',
    model: vehicleData.model || '',
    year: vehicleData.year || '',
  });
};

/**
 * Track cart abandonment (when user has items in cart but leaves)
 */
export const trackCartAbandonment = (cartData: CartData) => {
  trackEvent('cart_abandonment', {
    currency: 'USD',
    value: parseFloat(String(cartData.total)) || 0,
    items_count: cartData.itemCount || 0,
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });
};

/**
 * Track when user is about to exit the site
 */
export const trackExitIntent = () => {
  if (typeof window === 'undefined') return;
  trackEvent('exit_intent', {
    page_location: window.location.pathname,
    time_on_page: Math.round((Date.now() - window.pageStartTime) / 1000) || 0,
  });
};

/**
 * Track scroll depth (to measure engagement)
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 */
export const trackScrollDepth = (percentage: number) => {
  trackEvent('scroll_depth', {
    percentage: percentage,
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });
};

/**
 * Track Pedal Power Kit / Throttle Controller page view
 * This helps track interest in throttle controller products specifically
 */
export const trackThrottlePageView = () => {
  trackEvent('throttle_page_view', {
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    product_category: 'Throttle Controllers',
    page_title: 'Pedal Power Kit',
  });
};

/**
 * Track Throttle Controller product view (GA4 eCommerce)
 * @param {object} product - Product details
 */
export const trackThrottleProductView = (product: Product) => {
  // Google Analytics 4 - specific throttle controller event
  trackEvent('view_item', {
    currency: 'USD',
    value: parseFloat(String(product.price)) || 0,
    items: [
      {
        item_id: product.gfu_part_num || product.id,
        item_name: product.title || 'Throttle Controller',
        item_brand: 'Green Filter',
        item_category: 'Throttle Controllers',
        item_category2: 'Pedal Power Kit',
        price: parseFloat(String(product.price)) || 0,
        quantity: 1,
      },
    ],
  });

  // Also track as custom throttle event for easier filtering
  trackEvent('throttle_product_view', {
    product_id: product.gfu_part_num || product.id,
    product_name: product.title,
    product_category: 'Throttle Controllers',
  });

  // Facebook Pixel
  trackFacebookViewContent({
    id: product.gfu_part_num || product.id,
    title: product.title,
    price: product.price,
    category: 'Throttle Controllers',
  });
};

/**
 * Track Throttle Controller vehicle search
 * @param {object} vehicleData - Vehicle filter data
 */
export const trackThrottleVehicleSearch = (vehicleData: VehicleData) => {
  trackEvent('throttle_vehicle_search', {
    make: vehicleData.make || '',
    model: vehicleData.model || '',
    year: vehicleData.year || '',
    product_category: 'Throttle Controllers',
  });
};

/**
 * Track blog post view
 * @param {object} blogData - Blog post details
 */
export const trackBlogView = (blogData: {
  slug: string;
  title: string;
  category?: string;
  readTime?: string;
}) => {
  trackEvent('blog_view', {
    blog_slug: blogData.slug,
    blog_title: blogData.title,
    blog_category: blogData.category || 'Uncategorized',
    read_time: blogData.readTime || '',
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });
};

/**
 * Track blog post read completion (when user reaches end of article)
 * @param {object} blogData - Blog post details
 */
export const trackBlogRead = (blogData: {
  slug: string;
  title: string;
  category?: string;
  timeSpent?: number;
}) => {
  trackEvent('blog_read', {
    blog_slug: blogData.slug,
    blog_title: blogData.title,
    blog_category: blogData.category || 'Uncategorized',
    time_spent_seconds: blogData.timeSpent || 0,
    page_location: typeof window !== 'undefined' ? window.location.pathname : '',
  });
};

/**
 * Initialize exit intent tracking (call this on app mount)
 */
export const initExitTracking = () => {
  // Only run in browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Track page start time for time-on-page calculation
  window.pageStartTime = Date.now();

  // Track exit intent when mouse leaves viewport towards top
  let exitIntentFired = false;
  document.addEventListener('mouseout', (e: MouseEvent) => {
    if (!exitIntentFired && !(e as any).toElement && !e.relatedTarget && e.clientY < 10) {
      exitIntentFired = true;
      trackExitIntent();
    }
  });

  // Track when user switches tabs or minimizes browser
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      trackEvent('tab_hidden', {
        page_location: window.location.pathname,
        time_on_page: Math.round((Date.now() - window.pageStartTime) / 1000) || 0,
      });
    }
  });

  // Track scroll depth milestones, throttled to once per animation frame
  const scrollMilestones: Record<number, boolean> = { 25: false, 50: false, 75: false, 100: false };
  let scrollRafId: number | null = null;
  window.addEventListener(
    'scroll',
    () => {
      if (scrollRafId !== null) return;
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = null;
        const scrollPercentage = Math.round(
          ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
        );

        Object.keys(scrollMilestones).forEach((milestone) => {
          const milestoneNum = parseInt(milestone);
          if (!scrollMilestones[milestoneNum] && scrollPercentage >= milestoneNum) {
            scrollMilestones[milestoneNum] = true;
            trackScrollDepth(milestoneNum);
          }
        });
      });
    },
    { passive: true }
  );
};
